// Per-EHR onboarding configuration.
//
// Add a new EHR by appending a new entry to `EHR_ONBOARDING_REGISTRY`.
// The generic <EhrOnboardingCard /> reads from this registry to render the
// correct credential + onboarding fields, run the right hooks, and show the
// right helper copy. No special-case branches per EHR in the rendering code.

// Shared US-clinic timezone options. Matches the list used elsewhere in the
// app (Settings, CSV upload) so admins see a familiar dropdown.
export const EHR_TIMEZONE_OPTIONS = [
  { value: 'US/Eastern', label: 'US/Eastern' },
  { value: 'US/Central', label: 'US/Central' },
  { value: 'US/Mountain', label: 'US/Mountain' },
  { value: 'US/Pacific', label: 'US/Pacific' },
  { value: 'US/Alaska', label: 'US/Alaska' },
  { value: 'US/Hawaii', label: 'US/Hawaii' },
  { value: 'America/Puerto_Rico', label: 'US/Atlantic' },
  { value: 'Pacific/Pago_Pago', label: 'US/Samoa' },
  { value: 'UTC', label: 'UTC' },
];

// Reusable "Timezone" onboarding field. Spread it into any EHR's
// `onboarding.fields` to ask for a timezone. Default is US/Eastern, the
// most common US clinic timezone.
export const TIMEZONE_ONBOARDING_FIELD = {
  key: 'timezone',
  label: 'Timezone',
  type: 'select',
  required: true,
  options: EHR_TIMEZONE_OPTIONS,
  defaultValue: 'US/Eastern',
  helperText:
    "Clinic's local timezone. Drives appointment sync windows and bulk upload defaults.",
};
//
// Schema reference:
//
//   type CredentialField = {
//     key: string;            // payload field name
//     label: string;          // form label
//     type?: 'text' | 'password'; // default 'text'
//     placeholder?: string;
//     required?: boolean;     // default true
//     helperText?: string;
//   };
//
//   type OnboardingField = CredentialField;
//
//   type EhrEntry = {
//     ehrSystem: string;         // backend EHR system slug (e.g. 'athena')
//     label: string;             // human label (sub-tab title)
//     iconUrl?: string;          // optional logo for the tab
//     credentials?: {
//       fields: CredentialField[];
//       // Some EHRs (e.g. OP) write the same credentials under multiple
//       // ehr_system keys. Default is just `[entry.ehrSystem]`.
//       ehrSystems?: string[];
//     } | null;                  // null means "no credentials push needed".
//     onboarding: {
//       fields: OnboardingField[];
//     };
//     howTo?: ReactNode;         // optional rich help text shown in a collapse
//   };

export const EHR_ONBOARDING_REGISTRY = [
  {
    ehrSystem: 'athena',
    label: 'Athena',
    iconUrl: 'https://images.insighthealth.ai/ehr-logos/athena.svg',
    credentials: null,
    onboarding: {
      fields: [
        {
          key: 'practice_id',
          label: 'Practice ID',
          type: 'text',
          required: true,
          placeholder: 'e.g. 195900',
          helperText:
            'Athena Practice ID assigned to this clinic. Persisted on the organization as ehr_id.',
        },
        TIMEZONE_ONBOARDING_FIELD,
      ],
    },
    howTo: (
      <div className="text-sm text-gray-700 flex flex-col gap-2">
        <div>
          Athena uses shared Insight Health API credentials configured in the
          backend, so no per-org credentials need to be entered here. Onboarding
          only needs the clinic&apos;s <strong>Practice ID</strong> and{' '}
          <strong>timezone</strong>.
        </div>
        <div className="text-xs text-gray-500">
          The Practice ID determines which Athena practice we subscribe to for
          appointment change webhooks and pull from for departments/providers.
        </div>
      </div>
    ),
  },
  {
    ehrSystem: 'advancedmd',
    label: 'AdvancedMD',
    iconUrl: 'https://images.insighthealth.ai/ehr-logos/advancedmd.svg',
    credentials: {
      fields: [
        {
          key: 'username',
          label: 'Username',
          type: 'text',
          required: true,
          placeholder: 'AdvancedMD API user',
        },
        {
          key: 'password',
          label: 'Password',
          type: 'password',
          required: true,
        },
        {
          key: 'office_code',
          label: 'Office Code',
          type: 'text',
          required: true,
          placeholder: 'e.g. 123456',
        },
        {
          key: 'app_name',
          label: 'App Name',
          type: 'text',
          required: true,
          placeholder: 'e.g. InsightHealth',
        },
      ],
    },
    onboarding: {
      fields: [
        {
          key: 'practice_id',
          label: 'Practice ID',
          type: 'text',
          required: true,
          placeholder: 'AdvancedMD practice / office identifier',
          helperText:
            'AdvancedMD Practice ID for this clinic. Persisted on the organization as ehr_id.',
        },
        TIMEZONE_ONBOARDING_FIELD,
      ],
    },
    howTo: (
      <div className="text-sm text-gray-700 flex flex-col gap-2">
        <div>
          Provision a dedicated AdvancedMD API user for Insight Health and enter
          its credentials below. The same App Name must be whitelisted on the
          AdvancedMD side. Pick the clinic&apos;s local timezone so appointment
          sync windows match office hours.
        </div>
        <div className="text-xs text-gray-500">
          Credentials are pushed first (POST /v2/ehr/credentials), then the
          organization is onboarded so default sync settings, the Frontegg
          service user, and ehr_id are written.
        </div>
      </div>
    ),
  },
];

export const getEhrOnboardingEntry = (ehrSystem) =>
  EHR_ONBOARDING_REGISTRY.find((e) => e.ehrSystem === ehrSystem);
